/**
 *  @class
 *  @function RecentlyViewedProducts
 */
if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();

      this.url = theme.routes.search_url + '?view=recently-viewed&type=product&q=';
      this.container = this.querySelector('.products');
      this.mobileControls = this.querySelector('[data-recently-viewed-mobile-controls]');
      this.currentId = this.dataset.productHandle;
      this.max = 8;
      this.products = '';
      if (window.localStorage) {
        let recentIds = window.localStorage.getItem('recently-viewed');
        if (recentIds && typeof (recentIds) !== undefined) {
          window.recentlyViewedIds = JSON.parse(recentIds);
        }
      }
    }
    connectedCallback() {
      this.abortController = new AbortController();
      this.buildUrl();
    }
    disconnectedCallback() {
      this.abortController?.abort();
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }
      if (this.mobileControls && this._mobileControlsClickHandler) {
        this.mobileControls.removeEventListener('click', this._mobileControlsClickHandler);
      }
      this._mobileControlsClickHandler = null;
      if (this.container && this._slideHeightUpdateHandler) {
        this.container.removeEventListener('load', this._slideHeightUpdateHandler, true);
      }
      if (this._equalizeRAF) {
        cancelAnimationFrame(this._equalizeRAF);
        this._equalizeRAF = null;
      }
      if (this._flkty) {
        if (this._onFlickityChange) {
          this._flkty.off('change', this._onFlickityChange);
        }
        this._flkty.destroy();
        this._flkty = null;
      }
    }
    buildUrl() {
      let i = 0;
      window.recentlyViewedIds.forEach((val) => {
        if (val === this.currentId) {
          return;
        }
        if (i >= this.max) {
          return;
        }
        this.products += 'handle:' + val + ' OR ';
        i++;
      });

      this.url = this.url + encodeURIComponent(this.products);

      this.fetchProducts();
    }
    fetchProducts() {
      fetch(this.url, { signal: this.abortController.signal })
        .then(response => response.text())
        .then(text => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('.products');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.renderCarousel(recommendations.innerHTML);
          }
        })
        .catch(e => {
          if (e.name !== 'AbortError') console.error(e);
        });
    }

    renderCarousel(slidesMarkup) {
      if (!this.container) return;

      this._slideHeightsReady = false;
      this._equalizedHeight = 0;
      this.style.display = 'block';
      this.style.visibility = 'hidden';

      const prevButton = this.container.querySelector('.flickity-prev');
      const nextButton = this.container.querySelector('.flickity-next');
      this.prevButton = prevButton;
      this.nextButton = nextButton;

      this.container.querySelectorAll('.carousel__slide').forEach((el) => el.remove());

      const fragment = document.createElement('div');
      fragment.innerHTML = slidesMarkup;
      Array.from(fragment.querySelectorAll('.carousel__slide')).forEach((slide) => {
        this.container.insertBefore(slide, prevButton || null);
      });

      const slideCount = this.container.querySelectorAll('.carousel__slide').length;
      this._showNav = slideCount >= 5;
      this._showMobileControls = slideCount > 1;
      this.classList.toggle('recently-viewed-products--no-pagination', !this._showNav);
      if (prevButton) prevButton.classList.toggle('hide-arrow', !this._showNav);
      if (nextButton) nextButton.classList.toggle('hide-arrow', !this._showNav);

      this.waitForWidth(() => {
        if (typeof Flickity === 'undefined') return;

        this.unveilCarouselImages();
        this.waitForCarouselImages().then(() => {
          if (this._flkty) {
            this._flkty.destroy();
            this._flkty = null;
          }

          this._flkty = new Flickity(this.container, {
            cellSelector: '.carousel__slide',
            contain: true,
            pageDots: this._showNav,
            prevNextButtons: false,
            wrapAround: false,
            cellAlign: 'left',
            groupCells: true
          });

          if (prevButton) {
            prevButton.addEventListener('click', () => this._flkty.previous());
          }
          if (nextButton) {
            nextButton.addEventListener('click', () => this._flkty.next());
          }

          this._resizeHandler = () => {
            this._equalizedHeight = 0;
            this.equalizeSlideHeights();
            if (this._showMobileControls) {
              this.renderMobileDots();
            }
          };
          window.addEventListener('resize', this._resizeHandler);

          this._onFlickityChange = () => {
            if (this._showMobileControls) this.renderMobileDots();
          };
          this._flkty.on('change', this._onFlickityChange);
          this.initMobileControls();
          requestAnimationFrame(() => {
            this.equalizeSlideHeights(() => {
              this.style.visibility = '';
              this.style.display = '';
              this.classList.add('product-recommendations--loaded');
              this.bindSlideHeightUpdates();
            }, { reset: false });
          });
        });
      });
    }

    unveilCarouselImages() {
      if (!this.container || typeof lazySizes === 'undefined') return;

      this.container.querySelectorAll('.lazyload').forEach((img) => {
        lazySizes.loader.unveil(img);
      });
    }

    waitForCarouselImages(timeout = 4000) {
      if (!this.container) return Promise.resolve();

      const imgs = this.container.querySelectorAll('img');
      if (!imgs.length) return Promise.resolve();

      const loads = Array.from(imgs).map((img) => {
        if (img.classList.contains('lazyloaded') || (!img.classList.contains('lazyload') && img.complete && img.naturalHeight > 0)) {
          return Promise.resolve();
        }
        return new Promise((resolve) => {
          const finish = () => resolve();
          img.addEventListener('lazyloaded', finish, { once: true });
          img.addEventListener('load', finish, { once: true });
          img.addEventListener('error', finish, { once: true });
        });
      });

      return Promise.race([
        Promise.all(loads),
        new Promise((resolve) => setTimeout(resolve, timeout))
      ]);
    }

    bindSlideHeightUpdates() {
      if (!this.container) return;

      if (!this._slideHeightUpdateHandler) {
        this._slideHeightUpdateHandler = () => {
          if (this._equalizeRAF) cancelAnimationFrame(this._equalizeRAF);
          this._equalizeRAF = requestAnimationFrame(() => {
            this.equalizeSlideHeights(null, { reset: false, growOnly: true });
          });
        };
      }

      this.container.removeEventListener('load', this._slideHeightUpdateHandler, true);
      this.container.addEventListener('load', this._slideHeightUpdateHandler, true);
    }

    initMobileControls() {
      if (!this.mobileControls || !this._flkty) return;
      if (!this._showMobileControls) {
        this.mobileControls.classList.remove('is-ready');
        const wrap = this.mobileControls.querySelector('[data-mobile-dots]');
        if (wrap) wrap.innerHTML = '';
        return;
      }

      if (!this._mobileControlsClickHandler) {
        this._mobileControlsClickHandler = (event) => {
          const nav = event.target.closest('[data-mobile-nav]');
          if (nav) {
            if (nav.getAttribute('data-mobile-nav') === 'prev') {
              this._flkty.previous();
            } else {
              this._flkty.next();
            }
            return;
          }

          const dot = event.target.closest('[data-mobile-dot-index]');
          if (!dot) return;

          const index = parseInt(dot.getAttribute('data-mobile-dot-index'), 10);
          if (isNaN(index)) return;
          this._flkty.select(index);
        };
        this.mobileControls.addEventListener('click', this._mobileControlsClickHandler);
      }

      this.renderMobileDots();
    }

    renderMobileDots() {
      if (!this.mobileControls || !this._flkty) return;
      if (!this._showMobileControls) {
        this.mobileControls.classList.remove('is-ready');
        const w = this.mobileControls.querySelector('[data-mobile-dots]');
        if (w) w.innerHTML = '';
        return;
      }

      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (!isMobile) {
        this.mobileControls.classList.remove('is-ready');
        const wrap = this.mobileControls.querySelector('[data-mobile-dots]');
        if (wrap) wrap.innerHTML = '';
        return;
      }

      const dotsWrap = this.mobileControls.querySelector('[data-mobile-dots]');
      if (!dotsWrap) return;

      const total = this._flkty.slides ? this._flkty.slides.length : 0;
      const selected = this._flkty.selectedIndex;
      if (total <= 1) {
        dotsWrap.innerHTML = '';
        this.mobileControls.classList.remove('is-ready');
        return;
      }

      let html = '';
      for (let i = 0; i < total; i++) {
        html += '<button class="recently-viewed-products__mobile-dot' + (i === selected ? ' is-selected' : '') + '" type="button" data-mobile-dot-index="' + i + '" aria-label="Go to recently viewed slide ' + (i + 1) + '"></button>';
      }
      dotsWrap.innerHTML = html;
      this.mobileControls.classList.add('is-ready');
    }

    equalizeSlideHeights(done, options = {}) {
      const reset = options.reset !== false;
      const growOnly = options.growOnly === true;
      const slides = this.container ? this.container.querySelectorAll('.carousel__slide') : [];
      if (!slides.length) {
        if (typeof done === 'function') done();
        return;
      }

      if (reset) {
        this._equalizedHeight = 0;
        slides.forEach((slide) => {
          slide.style.minHeight = '';
        });
        if (this._flkty) {
          this._flkty.resize();
        }
      }

      requestAnimationFrame(() => {
        let max = 0;
        slides.forEach((slide) => {
          max = Math.max(max, slide.offsetHeight);
        });

        const currentHeight = this._equalizedHeight || 0;
        if (growOnly && max <= currentHeight) {
          if (typeof done === 'function') done();
          return;
        }

        if (max > 0) {
          this._equalizedHeight = max;
          slides.forEach((slide) => {
            slide.style.minHeight = max + 'px';
          });
          if (this._flkty) {
            this._flkty.resize();
          }
        }
        this.centerArrowsOnImage();
        this._slideHeightsReady = true;
        if (typeof done === 'function') done();
      });
    }

    centerArrowsOnImage() {
      if (!this._flkty || !this.prevButton || !this.nextButton) return;
      if (!this._flkty.cells || !this._flkty.cells.length) return;

      const firstCell = this._flkty.cells[0];
      const featuredImage = firstCell.element.querySelector('.product-featured-image');
      if (!featuredImage) return;

      let maxHeight = 0;
      const imageHeight = featuredImage.clientHeight;
      this._flkty.cells.forEach((cell) => {
        if (cell.size.height > maxHeight) {
          maxHeight = cell.size.height;
        }
      });

      if (maxHeight > imageHeight) {
        const difference = (maxHeight - imageHeight) / -2;
        this.prevButton.style.transform = 'translateY(' + difference + 'px)';
        this.nextButton.style.transform = 'translateY(' + difference + 'px)';
      } else {
        this.prevButton.style.transform = '';
        this.nextButton.style.transform = '';
      }
    }

    waitForWidth(callback, attempt = 0) {
      if (!this.container || attempt > 30) return;

      const width = this.container.offsetWidth || (this.closest('.columns') || this).offsetWidth;
      if (width > 0) {
        callback();
        return;
      }

      requestAnimationFrame(() => this.waitForWidth(callback, attempt + 1));
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
